import React, { useState, useRef, useCallback } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

// Design system constants
const colors = {
  white: '#ffffff',
  gray50: '#f9f9f9',
  gray200: '#e6e6e6',
  gray300: '#d1d1d1',
  gray500: '#8e8e8e',
  gray700: '#636363',
  gray900: '#333333',
};

const fontStyles = {
  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
  medium: {
    fontWeight: 500,
  },
};

const GoogleLoginButton = ({ className, onSuccess: customOnSuccess, text = "Sign in with Google", disabled = false }) => {
  const { loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState('');
  const [isPressed, setIsPressed] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const googleLoginRef = useRef(null);
  
  const handleSuccess = useCallback(async (credentialResponse) => {
    if (isLoading) return; // Prevent double-submission
    
    try {
      setIsLoading(true);
      setErrorMessage(''); // Clear any previous errors
      console.log('Google login successful, credential received');
      
      // If a custom success handler is provided, use it instead of loginWithGoogle
      if (customOnSuccess) {
        await customOnSuccess(credentialResponse);
      } else {
        // Otherwise, use the default login flow
        const userData = await loginWithGoogle(credentialResponse);
        console.log('Login successful, user data received:', userData);
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Login failed:', error);
      
      // Check for beta access error specifically
      if (error.response && error.response.data && error.response.data.error === 'beta_access_required') {
        // Set beta access error message
        setErrorMessage(error.response.data.message || 'We are currently in beta and not accepting new users.');
      } else if (error.response && error.response.data && error.response.data.error === 'signup_disabled') {
        // Handle signup disabled error with demo booking message
        setErrorMessage('You don\'t have access to SignPact yet. Book a demo and get official onboarding from our founder to get started!');
      } else {
        // More detailed error information
        if (error.response) {
          console.error('Error response:', error.response.data);
          console.error('Status code:', error.response.status);
        }
        
        setErrorMessage('You don\'t have access to SignPact yet. Book a demo and get official onboarding from our founder to get started!');
      }
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, customOnSuccess, loginWithGoogle, navigate]);
  
  const handleError = () => {
    console.error('Google login failed');
    setErrorMessage('You don\'t have access to SignPact yet. Book a demo and get official onboarding from our founder to get started!');
  };
  
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  // Validate client ID and show warning in development
  if (!clientId && process.env.NODE_ENV === 'development') {
    console.warn('⚠️ REACT_APP_GOOGLE_CLIENT_ID not found in environment variables');
  }

  // Manual click handler for triggering Google login
  const handleManualClick = useCallback(() => {
    if (disabled || isLoading) return;
    
    // Trigger the hidden Google login button
    if (googleLoginRef.current) {
      const googleButton = googleLoginRef.current.querySelector('div[role="button"]');
      if (googleButton) {
        googleButton.click();
      } else {
        console.warn('Google login button not found in DOM');
      }
    }
  }, [disabled, isLoading]);

  // Dynamic button styles based on state
  const getButtonStyle = () => {
    let backgroundColor = colors.white;
    let transform = 'none';
    let boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';

    if (disabled || isLoading) {
      backgroundColor = colors.gray50;
    } else if (isPressed) {
      backgroundColor = colors.gray200;
      transform = 'translateY(1px)';
      boxShadow = '0 1px 2px rgba(0, 0, 0, 0.1)';
    } else if (isHovered) {
      backgroundColor = colors.gray50;
      boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
    }

    return {
      backgroundColor,
      transform,
      boxShadow,
      color: (disabled || isLoading) ? colors.gray500 : colors.gray900,
      border: '1px solid #e6e6e6', // Exact match with email button
      borderRadius: '0.75rem', // 12px - matches rounded-xl from email button
      padding: '0.75rem 1.5rem', // 12px 24px - matches py-3 px-6 from email button
      width: '100%',
      height: 'auto',
      minHeight: '48px', // Ensure consistent height
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: (disabled || isLoading) ? 'not-allowed' : 'pointer',
      transition: 'all 0.2s ease', // Match email button transition timing
      position: 'relative',
      outline: 'none',
      fontSize: '16px',
      fontWeight: 500,
      lineHeight: '1.25',
      ...fontStyles,
    };
  };
  
  return (
    <div className={className}>
      {errorMessage && (
        <div className="error-message bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {errorMessage}
        </div>
      )}
      
      {/* Custom styled Google button that manually triggers Google login */}
      <div
        style={getButtonStyle()}
        onClick={handleManualClick}
        onMouseEnter={() => !(disabled || isLoading) && setIsHovered(true)}
        onMouseLeave={() => {
          setIsHovered(false);
          setIsPressed(false);
        }}
        onMouseDown={() => !(disabled || isLoading) && setIsPressed(true)}
        onMouseUp={() => setIsPressed(false)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleManualClick();
          }
        }}
      >
        {/* Google Logo SVG */}
        <svg 
          width="20" 
          height="20" 
          viewBox="0 0 24 24" 
          style={{ marginRight: '8px', flexShrink: 0 }}
        >
          <path 
            fill="#4285F4" 
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path 
            fill="#34A853" 
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path 
            fill="#FBBC05" 
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path 
            fill="#EA4335" 
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
        
        {/* Button text with loading state */}
        <span style={{ 
          fontSize: '16px', 
          lineHeight: '1.25',
          fontWeight: 500,
          letterSpacing: '-0.01em'
        }}>
          {isLoading ? 'Signing in...' : text}
        </span>
      </div>

      {/* Hidden Google Login component - completely invisible and positioned offscreen */}
      <div 
        ref={googleLoginRef}
        style={{
          position: 'absolute',
          left: '-9999px',
          top: '-9999px',
          visibility: 'hidden',
          pointerEvents: 'none',
        }}
      >
        <GoogleLogin
          clientId={clientId}
          onSuccess={handleSuccess}
          onError={handleError}
          useOneTap={false}
          cookiePolicy={'single_host_origin'}
          theme="outline"
          shape="rectangular"
          text="signin_with"
          size="large"
          locale="en"
          context="signin"
          ux_mode="popup"
          type="standard"
          logo_alignment="left"
          auto_select={false}
          itp_support={false}
          cancel_on_tap_outside={true}
          disabled={disabled || isLoading}
        />
      </div>
    </div>
  );
};

export default GoogleLoginButton;